import { useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Badge, Banner, Button, Card, EmptyState, Field, PageBody, PageHeader, StackedList } from "../ui/Field";

type Passkey = {
  id: string;
  device_label: string | null;
  transports: string | null;
  backed_up: number;
  last_used_at: number | null;
  created_at: number;
};

export default function Account() {
  const { me } = useAuth();
  const [list, setList] = useState<Passkey[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await api.get<{ items: Passkey[] }>("/api/auth/passkeys");
      setList(r.items);
    } catch (e) { setErr((e as Error).message); }
  };

  useEffect(() => { load(); }, []);

  async function addPasskey() {
    setErr(null);
    try {
      const options = await api.post<Parameters<typeof startRegistration>[0]["optionsJSON"]>("/api/auth/passkeys/add/options", {});
      const attestation = await startRegistration({ optionsJSON: options });
      await api.post("/api/auth/passkeys/add/verify", { attestation });
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  async function remove(id: string) {
    if (!confirm("Remove this passkey?")) return;
    try { await api.delete(`/api/auth/passkeys/${encodeURIComponent(id)}`); await load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ Account"
        title={<>Your <em>identity</em></>}
        description="Manage your profile and the passkeys that authenticate you."
      />

      {err && <Banner kind="error">{err}</Banner>}

      <div className="grid gap-6 md:grid-cols-2 mb-6">
        <Card number="A1" title="Profile">
          <Field label="Email" value={me?.user?.email ?? ""} disabled />
          <Field
            label="Display name"
            value={me?.user?.display_name ?? ""}
            placeholder="—"
            disabled
            help="Set during invite acceptance. Inline edit coming."
          />
        </Card>
        <Card number="A2" title="Identity">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[var(--color-ink)] text-[var(--color-paper)] flex items-center justify-center display italic text-3xl">
              {(me?.user?.email ?? "?").slice(0, 1).toUpperCase()}
            </div>
            <div>
              <div className="text-[14px] text-[var(--color-ink)]">{me?.user?.email}</div>
              {me?.user?.is_superuser && <Badge tone="accent" className="mt-1">Superuser</Badge>}
            </div>
          </div>
        </Card>
      </div>

      <Card
        number="A3"
        title="Passkeys"
        description="Each device you use should have its own passkey."
        actions={<Button size="sm" variant="ghost" onClick={addPasskey}>+ Add passkey</Button>}
        bodyClassName={list.length === 0 ? "p-6" : "p-0"}
      >
        {list.length === 0 ? (
          <EmptyState title="No passkeys registered." description="Click Add passkey to register this device." action={<Button onClick={addPasskey}>Add passkey</Button>} />
        ) : (
          <StackedList className="px-5">
            {list.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-4 py-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-medium text-[var(--color-ink)]">{p.device_label ?? "Unnamed device"}</span>
                    {p.backed_up ? <Badge tone="success">Synced</Badge> : <Badge tone="neutral">Local</Badge>}
                  </div>
                  <div className="text-[12px] text-[var(--color-muted)] mt-0.5">
                    Created {new Date(p.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    {p.last_used_at && ` · Last used ${timeAgo(p.last_used_at)}`}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(p.id)}>Remove</Button>
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}
